/**
 * One quantity, broken down over the subsystems that make it up.
 *
 * Inline SVG, like the hierarchy diagrams, because the front end carries no
 * charting dependency and a horizontal bar chart is a hundred lines of geometry
 * that is already tested in `chart-geometry.ts`.
 *
 * A bar chart rather than the pie that was asked for, and the reason is the
 * payload rather than taste. A pie asserts that its slices are a whole: the
 * angles have to add to 360 degrees, so a subsystem whose mass NOBODY HAS STATED
 * cannot appear in one at all. It would silently vanish and the remaining slices
 * would grow to fill its place - the chart would look complete precisely when the
 * budget is not. Bars have no such closure: a row with an unknown extent is still
 * a row, drawn as a dashed rule with the words `not stated` where its number
 * would be. Sorted biggest first, they also answer "where is the mass" more
 * directly than a pie, which is what somebody opens this page to ask.
 *
 * Three marks carry the four states of a total, and text carries the difference
 * the marks cannot:
 *
 *   - a **solid** bar is a figure, and nothing else gets one;
 *   - a **hatched** bar is a total with a caveat - a floor, or partial because
 *     something below it states nothing. The value beside it says which: `≥` for a
 *     floor, and the footnote names the parts responsible for a partial;
 *   - a **dashed rule** is a total that is not knowable at all. Not a
 *     zero-length bar: unset and zero are opposite claims and must not be one
 *     picture. The example's structure genuinely draws 0 W, and it gets a real
 *     zero.
 *
 * Identity never rests on colour: every bar is the same hue, and every row
 * carries its name and its value as text.
 */

import { useId } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import type { Breakdown } from './budget-model'
import { formatTotal, unitLabel } from './budget-model'
import { BAR_HEIGHT, CHART_WIDTH, PLOT_WIDTH, barLayout } from './chart-geometry'
import { seriesColor } from './palette'

export const HATCH_NOTE =
  'A hatched bar is not a figure: it is a floor, or a total something below it ' +
  'does not state. A dashed rule is a quantity nothing states at all — which is ' +
  'not zero.'

/**
 * The legend, which is what makes the hatching mean something.
 *
 * `hatchId` is threaded in rather than fixed, because a page renders one of these
 * per quantity and two `<pattern>` elements sharing an id is a document with two
 * answers to the same reference.
 */
function ChartLegend({
  colour,
  surface,
  hatchId,
}: {
  colour: string
  surface: string
  hatchId: string
}) {
  return (
    <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
      {[
        { key: 'solid', label: 'A figure' },
        { key: 'hatched', label: 'A floor, or partial' },
        { key: 'none', label: 'Not stated' },
      ].map((entry) => (
        <Stack key={entry.key} direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
          <svg width={20} height={12} aria-hidden="true">
            {/*
              The swatch carries its own copy of the pattern rather than pointing
              at the chart's. A paint reference resolves across SVG roots in a
              document, but only where both are rendered - and a swatch that
              silently loses its fill is a legend that explains nothing.
            */}
            <defs>
              <pattern
                id={`${hatchId}-key`}
                width={6}
                height={6}
                patternUnits="userSpaceOnUse"
                patternTransform="rotate(45)"
              >
                <rect width={6} height={6} fill={surface} />
                <rect width={2.5} height={6} fill={colour} />
              </pattern>
            </defs>
            {entry.key === 'none' ? (
              <line
                x1={1}
                y1={6}
                x2={19}
                y2={6}
                stroke={colour}
                strokeWidth={1}
                strokeDasharray="3 2"
              />
            ) : (
              <>
                <rect x={0} y={1} width={20} height={10} rx={3} fill={surface} />
                <rect
                  x={0}
                  y={1}
                  width={20}
                  height={10}
                  rx={3}
                  fill={entry.key === 'solid' ? colour : `url(#${hatchId}-key)`}
                />
              </>
            )}
          </svg>
          <Typography variant="caption" color="text.secondary">
            {entry.label}
          </Typography>
        </Stack>
      ))}
    </Stack>
  )
}

export default function BreakdownChart({
  breakdown,
  rootName,
}: {
  breakdown: Breakdown
  /** The part this breaks down, named in the chart's own description. */
  rootName: string
}) {
  const theme = useTheme()
  const hatchId = `${useId()}-hatch`
  const colour = seriesColor(theme.palette.mode)
  const surface = theme.palette.background.paper
  const grid = theme.palette.divider
  const muted = theme.palette.text.secondary

  const layout = barLayout(breakdown.slices, breakdown.total?.total ?? null)
  const unit = unitLabel(breakdown.attribute.unit)
  const headline = formatTotal(breakdown.total)

  if (layout.rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {rootName} declares no parts inside it, so there is nothing to break{' '}
        {breakdown.attribute.label.toLowerCase()} down over. Its total is {headline}.
      </Typography>
    )
  }

  return (
    <Stack spacing={1}>
      <Box>
        <Typography variant="subtitle2">{breakdown.attribute.label} by subsystem</Typography>
        <Typography variant="caption" color="text.secondary">
          {rootName} totals {headline}. The bars are its subsystems, biggest first, against
          an axis in {unit || breakdown.attribute.unit}.
        </Typography>
      </Box>

      <ChartLegend colour={colour} surface={surface} hatchId={hatchId} />

      <Box
        component="svg"
        viewBox={`0 0 ${CHART_WIDTH} ${layout.height}`}
        preserveAspectRatio="xMinYMin meet"
        role="img"
        aria-label={`${breakdown.attribute.label} by subsystem of ${rootName}. ${layout.rows
          .map((row) => `${row.label}: ${row.value}`)
          .join('. ')}.`}
        sx={{ width: '100%', height: 'auto', display: 'block', overflow: 'visible' }}
      >
        <defs>
          {/*
            45-degree stripes of the series hue over the surface, so a caveated
            bar reads as less solid than a figure. Tone-on-surface rather than an
            outline: a border drawn round a mark adds ink that is not data.
          */}
          <pattern
            id={hatchId}
            width={6}
            height={6}
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(45)"
          >
            <rect width={6} height={6} fill={surface} />
            <rect width={2.5} height={6} fill={colour} />
          </pattern>
        </defs>

        {/* Gridlines: solid hairlines one step off the surface, never dashed. */}
        {layout.ticks.map((tick) => (
          <line
            key={`grid-${tick.value}`}
            x1={tick.x}
            y1={0}
            x2={tick.x}
            y2={layout.plotHeight}
            stroke={grid}
            strokeWidth={1}
          />
        ))}

        {layout.rows.map((row) => (
          <g key={row.key}>
            <text x={0} y={row.textY} fontSize={12} fill={muted}>
              {row.label}
            </text>
            <text
              x={CHART_WIDTH}
              y={row.textY}
              fontSize={12.5}
              textAnchor="end"
              fill={theme.palette.text.primary}
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {row.value}
            </text>
            {row.bar && row.bar.width > 0 && (
              <path
                d={row.bar.path}
                fill={row.mark === 'hatched' ? `url(#${hatchId})` : colour}
              />
            )}
            {row.isUnknown && (
              // The row exists and its extent is unknown. A dashed rule has no
              // height, so it cannot be misread as a very short bar.
              <line
                x1={0}
                y1={row.ruleY}
                x2={PLOT_WIDTH}
                y2={row.ruleY}
                stroke={muted}
                strokeWidth={1}
                strokeDasharray="4 3"
              />
            )}
            {row.bar && row.bar.width === 0 && (
              // A stated zero: a tick at the baseline, so the row is not blank.
              <line
                x1={0}
                y1={row.bar.y}
                x2={0}
                y2={row.bar.y + BAR_HEIGHT}
                stroke={colour}
                strokeWidth={2}
              />
            )}
          </g>
        ))}

        {/* The baseline every bar grows from. */}
        <line x1={0} y1={0} x2={0} y2={layout.plotHeight} stroke={muted} strokeWidth={1} />

        {layout.ticks.map((tick, at) => (
          <text
            key={`tick-${tick.value}`}
            x={tick.x}
            y={layout.height - 8}
            fontSize={11}
            fill={muted}
            textAnchor={at === 0 ? 'start' : at === layout.ticks.length - 1 ? 'end' : 'middle'}
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {tick.label}
          </text>
        ))}
      </Box>

      {breakdown.unstated.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          Partial: {breakdown.unstated.join(', ')} state no{' '}
          {breakdown.attribute.label.toLowerCase()}, so nothing was added for them. Unset is
          not zero, and the total above is what the model states rather than what the
          vehicle weighs.
        </Typography>
      )}
    </Stack>
  )
}
